import { Dispatch, SetStateAction, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, feedback, ICreateUser, ILogin, IUserView, sconAppService } from '../services';
import { Api } from '../services/api/axios-config';
import { setCsrfRequestToken } from '../services/api/axios-config/interceptors';
import { useAppThemeContext } from './ThemeContext';

interface IUserContextData {
  isAuthenticated: boolean;
  userId: number;
  userRoles: number[];
  setUserRoles: Dispatch<SetStateAction<number[]>>;
  userProfilePicture: string | undefined;
  setUserProfilePicture: Dispatch<SetStateAction<string | undefined>>;
  userName: string;
  setUserName: Dispatch<SetStateAction<string>>;
  userEmail: string;
  userPhone: string | undefined;
  emailConfirmed: boolean;
  setEmailConfirmed: Dispatch<SetStateAction<boolean>>;
  phoneConfirmed: boolean;
  setPhoneConfirmed: Dispatch<SetStateAction<boolean>>;
  whatsappSconappConfigured: boolean;
  validateConfirmedEmail: () => boolean;
  login: (user: ILogin) => Promise<void>;
  googleLogin: (jwt: string) => Promise<void>;
  create: (user: ICreateUser) => Promise<void>;
  logout: () => void;
}

const UserContext = createContext({} as IUserContextData);

export const useUserContext = () => {
  return useContext(UserContext);
};

export const LOCAL_STORAGE_KEY_USER_ID = 'ID';
export const LOCAL_STORAGE_KEY_USER_ROLES = 'ROLES';
export const LOCAL_STORAGE_KEY_USER_NAME = 'NAME';
export const LOCAL_STORAGE_KEY_USER_PROFILE_PICTURE = 'PROFILE_PICTURE';
export const LOCAL_STORAGE_KEY_USER_EMAIL_CONFIRMED = 'EMAIL_CONFIRMED';
export const LOCAL_STORAGE_KEY_USER_PHONE_CONFIRMED = 'PHONE_CONFIRMED';
export const LOCAL_STORAGE_KEY_USER_WHATSAPP_CONFIGURED = 'WHATSAPP_SCONAPP_CONFIGURED';
export const LOCAL_STORAGE_KEY_REDIRECT_URL = 'REDIRECT_URL';
export const LOCAL_STORAGE_KEY_USER_EMAIL = 'USER_EMAIL';
export const LOCAL_STORAGE_KEY_USER_PHONE = 'USER_PHONE';

export const setLocalStorage = (user?: IUserView) => {
  localStorage.setItem(LOCAL_STORAGE_KEY_USER_ID, JSON.stringify(user?.id || 0));
  localStorage.setItem(LOCAL_STORAGE_KEY_USER_ROLES, JSON.stringify(user?.userRoles || []));
  localStorage.setItem(LOCAL_STORAGE_KEY_USER_NAME, JSON.stringify(user?.name || ''));
  localStorage.setItem(LOCAL_STORAGE_KEY_USER_PROFILE_PICTURE, JSON.stringify(user?.profilePicture || ''));
  localStorage.setItem(LOCAL_STORAGE_KEY_USER_EMAIL, JSON.stringify(user?.email || ''));
  localStorage.setItem(LOCAL_STORAGE_KEY_USER_PHONE, JSON.stringify(user?.phone || ''));
  localStorage.setItem(LOCAL_STORAGE_KEY_USER_EMAIL_CONFIRMED, JSON.stringify(user?.emailConfirmed || false));
  localStorage.setItem(LOCAL_STORAGE_KEY_USER_PHONE_CONFIRMED, JSON.stringify(user?.phoneConfirmed || false));
};

export enum UserRoleEnum {
  Employee = 1,
  BusinessAdmin = 2,
  SystemAdmin = 3,
  Receptionist = 4
}

type Props = {
  children?: React.ReactNode,
};

export const UserProvider: React.FC<Props> = ({ children }) => {
  const { setThemeFromPreference } = useAppThemeContext();
  const [userId, setUserId] = useState(0);
  const [userRoles, setUserRoles] = useState<number[]>([]);
  const [userName, setUserName] = useState('');
  const [userProfilePicture, setUserProfilePicture] = useState<string>();
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState<string | undefined>();
  const [phoneConfirmed, setPhoneConfirmed] = useState(false);
  const [whatsappSconappConfigured, setWhatsappSconappConfigured] = useState(false);

  const navigate = useNavigate();

  const validateConfirmedEmail = () => {
    if (!emailConfirmed) {
      feedback('Confirme seu e-mail para utilizar esta funcionalidade.', 'error');
    }

    return emailConfirmed;
  };

  const setUserProps = (user?: IUserView) => {
    setUserId(user?.id || 0);
    setUserRoles(user?.userRoles || []);
    setUserName(user?.name || '');
    setUserProfilePicture(user?.profilePicture || '');
    setUserEmail(user?.email || '');
    setUserPhone(user?.phone || '');
    setEmailConfirmed(user?.emailConfirmed || false);
    setPhoneConfirmed(user?.phoneConfirmed || false);
  };

  useEffect(() => {
    const idUserStorage = localStorage.getItem(LOCAL_STORAGE_KEY_USER_ID);
    const userNameStorage = localStorage.getItem(LOCAL_STORAGE_KEY_USER_NAME);
    const userRolesStorage = localStorage.getItem(LOCAL_STORAGE_KEY_USER_ROLES);
    const userProfilePictureStorage = localStorage.getItem(LOCAL_STORAGE_KEY_USER_PROFILE_PICTURE);
    const userEmailStorage = localStorage.getItem(LOCAL_STORAGE_KEY_USER_EMAIL);
    const userPhoneStorage = localStorage.getItem(LOCAL_STORAGE_KEY_USER_PHONE);
    const emailConfirmedStorage = localStorage.getItem(LOCAL_STORAGE_KEY_USER_EMAIL_CONFIRMED);
    const phoneConfirmedStorage = localStorage.getItem(LOCAL_STORAGE_KEY_USER_PHONE_CONFIRMED);
    const whatsappConfiguredStorage = localStorage.getItem(LOCAL_STORAGE_KEY_USER_WHATSAPP_CONFIGURED);

    const user: IUserView = {
      id: idUserStorage ? JSON.parse(idUserStorage) as number : 0,
      userRoles: userRolesStorage ? JSON.parse(userRolesStorage) as number[] : [],
      name: userNameStorage ? JSON.parse(userNameStorage) as string : '',
      profilePicture: userProfilePictureStorage ? JSON.parse(userProfilePictureStorage) as string : '',
      email: userEmailStorage ? JSON.parse(userEmailStorage) as string : '',
      phone: userPhoneStorage ? JSON.parse(userPhoneStorage) as string : undefined,
      emailConfirmed: emailConfirmedStorage ? JSON.parse(emailConfirmedStorage) as boolean : false,
      phoneConfirmed: phoneConfirmedStorage ? JSON.parse(phoneConfirmedStorage) as boolean : false,
      firstName: '',
      gender: 0,
      appointments: []
    };

    // Restore WhatsApp connected status from localStorage (fast path)
    if (whatsappConfiguredStorage) {
      setWhatsappSconappConfigured(JSON.parse(whatsappConfiguredStorage) as boolean);
    }

    // Mostra dados do cache imediatamente (fast path para render inicial)
    setUserProps(user);

    // Só valida o cookie se havia uma sessão cacheada (user.id !== 0).
    // Se não havia, LoginPage já mostra o formulário de login em rotas
    // protegidas — não precisa de chamada API, e evita um loop de
    // redirect quando o cookie está expirado (ErrorInterceptor redireciona
    // para /home?expired=1, recarrega, user.id=0, e sem este guard o
    // UserContext chamaria /auth de novo → 401 → redirect → loop).
    const hadCachedSession = user.id !== 0;

    // Sempre busca dados frescos do JWT cookie em rotas protegidas
    const pathname = window.location.pathname;
    // Routes known to require authentication — anything else is public
    // (this allows /:slug public business page, /appointment walk-in, etc.)
    // Must stay in sync with LoginPage.tsx PROTECTED_ROUTE_PREFIXES.
    const PROTECTED_PREFIXES = ['/home', '/profile', '/schedule', '/business', '/employee',
      '/create-business', '/create-business-trial',
      '/subscription/checkout', '/subscription/success', '/subscription/pending',
      '/sconapp-whatsapp',
      '/onboarding', '/notifications',
      '/admin', '/admin/businesses',
      '/invite-employee',
    ];
    const isPublicRoute =
      pathname === '/' ||
      pathname === '/forgot-password' ||
      pathname.startsWith('/reset-password/') ||
      pathname.startsWith('/confirm-email/') ||
      !PROTECTED_PREFIXES.some(prefix =>
        pathname === prefix || pathname.startsWith(prefix + '/')
      );
    if (!isPublicRoute && hadCachedSession) {
      authService.getUserFromCookie()
        .then((userFromCookie) => {
          setLocalStorage(userFromCookie);
          setUserProps(userFromCookie);
          if (userFromCookie.themePreference) setThemeFromPreference(userFromCookie.themePreference);
        })
        .catch(() => {
          // Cookie is invalid or not present — clear cached state so LoginPage
          // can gate correctly even if ErrorInterceptor redirect is delayed/blocked.
          // Only redirect to /home if the user was previously authenticated
          // (had a cached userId). Fresh visits stay on the current page.
          const hadUserId = idUserStorage ? JSON.parse(idUserStorage) as number : 0;
          setLocalStorage();
          setUserProps();
          if (hadUserId) {
            navigate('/home');
          }
        });
    }

    // Re-fetch user data when the window regains focus (e.g. admin changed roles in another tab)
    const handleFocus = () => {
      // Só valida sessão se havia um userId cacheado — evita chamar /auth
      // desnecessariamente e causar loop de redirect (ErrorInterceptor redireciona
      // para /home?expired=1 ao receber 401).
      const storedId = localStorage.getItem(LOCAL_STORAGE_KEY_USER_ID);
      const hasCachedSession = storedId !== null && JSON.parse(storedId) as number !== 0;
      if (!hasCachedSession) return;

      const pathname = window.location.pathname;
      const isPublicRoute =
        pathname === '/' ||
        pathname === '/forgot-password' ||
        pathname.startsWith('/reset-password/') ||
        pathname.startsWith('/confirm-email/') ||
        !PROTECTED_PREFIXES.some(prefix =>
          pathname === prefix || pathname.startsWith(prefix + '/')
        );
      if (!isPublicRoute) {
        authService.getUserFromCookie()
          .then((userFromCookie) => {
            setLocalStorage(userFromCookie);
            setUserProps(userFromCookie);
            if (userFromCookie.themePreference) setThemeFromPreference(userFromCookie.themePreference);
          })
          .catch(() => {
            // Cookie is invalid or not present — clear cached state
            // (ErrorInterceptor will redirect on any subsequent 401)
            // Only redirect to /home if the user was previously authenticated.
            const idUserStorageFocus = localStorage.getItem(LOCAL_STORAGE_KEY_USER_ID);
            const hadUserIdFocus = idUserStorageFocus ? JSON.parse(idUserStorageFocus) as number : 0;
            setLocalStorage();
            setUserProps();
            if (hadUserIdFocus) {
              navigate('/home');
            }
          });
      }
    };
    window.addEventListener('focus', handleFocus);

    // Pre-fetch CSRF token on app load — store the request token (NOT the cookie value)
    // Note: response interceptor unwraps ApiResponse, so res.data IS the token string
    Api.get('/auth/antiforgery-token').then((res) => {
      if (res.data) setCsrfRequestToken(res.data as string);
    }).catch(() => undefined);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Fetch WhatsApp connected status when authenticated
  useEffect(() => {
    if (userId === 0) return;

    const cached = localStorage.getItem(LOCAL_STORAGE_KEY_USER_WHATSAPP_CONFIGURED);
    if (cached) {
      setWhatsappSconappConfigured(JSON.parse(cached) as boolean);
    }

    sconAppService.isConnected()
      .then((connected) => {
        setWhatsappSconappConfigured(connected);
        localStorage.setItem(LOCAL_STORAGE_KEY_USER_WHATSAPP_CONFIGURED, JSON.stringify(connected));
      })
      .catch(() => {
        // Assume disconnected on error
        setWhatsappSconappConfigured(false);
        localStorage.setItem(LOCAL_STORAGE_KEY_USER_WHATSAPP_CONFIGURED, JSON.stringify(false));
      });
  }, [userId]);

  const handleLogin = useCallback(async (login: ILogin) => {
    const result = await authService.login(login);

    if (result instanceof Error) {
      throw new Error(result.message);
    }

    setLocalStorage(result);
    setUserProps(result);
    if (result.themePreference) setThemeFromPreference(result.themePreference);
  }, [setThemeFromPreference]);

  const handleGoogleLogin = useCallback(async (jwt: string) => {
    const result = await authService.googleLogin(jwt);

    if (result instanceof Error) {
      throw new Error(result.message);
    }

    setLocalStorage(result);
    setUserProps(result);
    if (result.themePreference) setThemeFromPreference(result.themePreference);
  }, [setThemeFromPreference]);

  const handleRegister = useCallback(async (registerUser: ICreateUser) => {
    const result = await authService.create(registerUser);

    if (result instanceof Error) {
      throw new Error(result.message);
    }

    setLocalStorage(result);
    setUserProps(result);
    if (result.themePreference) setThemeFromPreference(result.themePreference);
  }, [setThemeFromPreference]);

  const handleLogout = useCallback(async () => {
    // Fetch a fresh CSRF token before logout — store the request token (NOT the cookie value)
    // Note: response interceptor unwraps ApiResponse, so res.data IS the token string
    try {
      const res = await Api.get('/auth/antiforgery-token');
      if (res.data) setCsrfRequestToken(res.data as string);
    } catch {
      // best-effort
    }
    await authService.logout();
    setLocalStorage();
    setUserProps();
    setWhatsappSconappConfigured(false);
    localStorage.removeItem(LOCAL_STORAGE_KEY_USER_WHATSAPP_CONFIGURED);
    localStorage.setItem(LOCAL_STORAGE_KEY_REDIRECT_URL, JSON.stringify(''));
    navigate('/home');
  }, []);

  const isAuthenticated = useMemo(() => userId !== 0, [userId]);

  return (
    <UserContext.Provider
      value={{
        isAuthenticated,
        userId,
        userRoles,
        setUserRoles,
        emailConfirmed,
        setEmailConfirmed,
        phoneConfirmed,
        setPhoneConfirmed,
        userEmail,
        userPhone,
        whatsappSconappConfigured,
        validateConfirmedEmail,
        userProfilePicture,
        setUserProfilePicture,
        userName,
        setUserName,
        login: handleLogin,
        googleLogin: handleGoogleLogin,
        create: handleRegister,
        logout: handleLogout
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
